import { X } from 'lucide-react';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { de } from '@/i18n/de';
import { fill } from '@/i18n/t';
import { Button, IconButton } from './button';

const DEFAULT_MS = 6_000;
const UNDO_MS = 10_000;
/** Höchstens so viele Meldungen gleichzeitig; die älteste weicht. */
const MAX_VISIBLE = 2;

export type ToastOptions = {
  message: string;
  /** Macht „Rückgängig“ verfügbar; die Meldung bleibt dann 10 Sekunden. */
  onUndo?: () => void;
  /**
   * Meldungen derselben Gruppe ersetzen sich (z. B. alle Handlungen auf Aufgaben): es steht nur die neueste da,
   * „Rückgängig“ gilt der neuesten Handlung.
   */
  group?: string;
};

type ToastItem = {
  id: number;
  message: string;
  group: string | undefined;
  /** Wie oft dieselbe Meldung kam (zusammengefasst). */
  count: number;
  /** Je zusammengefasster Handlung eine Umkehr; „Rückgängig“ ruft alle (neueste zuerst). */
  undos: readonly (() => void)[];
};

type ToastApi = { toast: (options: ToastOptions) => void };

const ToastContext = createContext<ToastApi | null>(null);

/** Gehört die neue Meldung zu einer schon sichtbaren (gleiche Gruppe oder gleicher Text samt Rückgängig-Art)? */
function matches(item: ToastItem, options: ToastOptions): boolean {
  if (options.group !== undefined || item.group !== undefined) {
    return item.group === options.group;
  }
  return (
    item.message === options.message && item.undos.length > 0 === (options.onUndo !== undefined)
  );
}

/** Fügt eine Meldung hinzu: fasst gleiche zusammen, ersetzt die der Gruppe, lässt höchstens zwei stehen. */
function addToast(
  current: readonly ToastItem[],
  options: ToastOptions,
  id: number,
): { next: readonly ToastItem[]; removed: readonly ToastItem[] } {
  const previous = current.find((item) => matches(item, options));
  const merged = previous !== undefined && options.group === undefined ? previous : undefined;
  const undo = options.onUndo === undefined ? [] : [options.onUndo];
  const item: ToastItem = {
    id,
    message: options.message,
    group: options.group,
    count: (merged?.count ?? 0) + 1,
    undos: [...(merged?.undos ?? []), ...undo],
  };
  const all = [...current.filter((entry) => entry !== previous), item];
  const dropped = all.slice(0, Math.max(0, all.length - MAX_VISIBLE));
  return {
    next: all.slice(-MAX_VISIBLE),
    removed: previous === undefined ? dropped : [previous, ...dropped],
  };
}

/**
 * Sammelstelle für Rückmeldungen. Die Meldungsfläche ist immer eingebaut, damit Ansagen zuverlässig ankommen.
 * Meldungen stapeln sich nicht: gleiche Meldungen werden zu einer mit Anzahl („Thema verworfen. (3-mal)“),
 * Meldungen derselben Gruppe ersetzen sich, und es stehen höchstens zwei zugleich da.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<readonly ToastItem[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const clearTimer = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const dismiss = useCallback(
    (id: number) => {
      clearTimer(id);
      setItems((current) => current.filter((item) => item.id !== id));
    },
    [clearTimer],
  );

  const toast = useCallback(
    (options: ToastOptions) => {
      const id = nextId.current++;
      setItems((current) => {
        const { next, removed } = addToast(current, options, id);
        for (const entry of removed) {
          clearTimer(entry.id);
        }
        return next;
      });
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), options.onUndo === undefined ? DEFAULT_MS : UNDO_MS),
      );
    },
    [clearTimer, dismiss],
  );

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) {
        clearTimeout(timer);
      }
      pending.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 bottom-24 z-50 flex flex-col items-center gap-2 px-4 sm:bottom-6"
      >
        {items.map((item) => (
          <div
            key={item.id}
            data-testid="meldung"
            className="pointer-events-auto flex max-w-md items-center gap-3 rounded-surface border border-line bg-surface-block py-2 pr-2 pl-4 text-body text-ink shadow-lg"
          >
            <span>
              {item.count > 1
                ? fill(de.ui.toastRepeated, { message: item.message, count: item.count })
                : item.message}
            </span>
            {item.undos.length === 0 ? null : (
              <Button
                variant="quiet"
                onClick={() => {
                  for (const undo of [...item.undos].reverse()) {
                    undo();
                  }
                  dismiss(item.id);
                }}
              >
                {item.undos.length > 1
                  ? fill(de.ui.undoAll, { count: item.undos.length })
                  : de.ui.undo}
              </Button>
            )}
            <IconButton variant="quiet" aria-label={de.ui.close} onClick={() => dismiss(item.id)}>
              <X aria-hidden="true" />
            </IconButton>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (api === null) {
    throw new Error('useToast muss innerhalb von ToastProvider verwendet werden');
  }
  return api;
}

/** Unsichtbare Ansagefläche für Statusänderungen („Zuletzt aktualisiert vor 5 Minuten“). */
export function LiveRegion({ children }: { children?: ReactNode }) {
  return (
    <div role="status" aria-live="polite" className="sr-only">
      {children}
    </div>
  );
}
